import { useState } from "react";
import { Info, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";

interface InfoTooltipProps {
  /** The title shown in the tooltip/modal header */
  title: string;
  /** The descriptive content */
  content: string;
  /** Optional aria-label for accessibility */
  ariaLabel?: string;
  /** Optional custom trigger element */
  trigger?: React.ReactNode;
  /** Size of the info icon */
  iconSize?: "sm" | "md";
}

/**
 * Reusable InfoTooltip component that displays informational content
 * Uses Popover on desktop and Dialog (modal) on mobile for better UX
 * Fully keyboard accessible and screen-reader friendly
 */
export function InfoTooltip({
  title,
  content,
  ariaLabel,
  trigger,
  iconSize = "sm",
}: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const defaultAriaLabel = ariaLabel || `More information about ${title}`;
  const iconClassName = iconSize === "sm" ? "h-4 w-4" : "h-5 w-5";

  const defaultTrigger = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-auto p-0.5 text-muted-foreground hover:text-primary hover:bg-transparent"
      aria-label={defaultAriaLabel}
    >
      <Info className={iconClassName} />
    </Button>
  );

  // On mobile, use a modal dialog for better touch interaction
  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center p-0.5 text-muted-foreground hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
          aria-label={defaultAriaLabel}
        >
          {trigger || <Info className={iconClassName} />}
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="h-5 w-5 text-primary" />
                {title}
              </DialogTitle>
              <DialogDescription className="text-base pt-2">
                {content}
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // On desktop, use a popover
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-4"
        side="right"
        align="start"
        sideOffset={8}
      >
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Info className="h-4 w-4 text-primary flex-shrink-0" />
              {title}
            </h4>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {content}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface InfoTooltipListProps {
  /** List of items with titles and descriptions */
  items: Array<{ label: string; description: string }>;
  /** Title for the info list */
  title: string;
  /** Optional aria-label for accessibility */
  ariaLabel?: string;
  /** Size of the info icon */
  iconSize?: "sm" | "md";
}

/**
 * InfoTooltipList shows all options with their explanations in a single tooltip/modal
 * Useful for dropdown fields where users need to understand all options before selecting
 */
export function InfoTooltipList({
  items,
  title,
  ariaLabel,
  iconSize = "sm",
}: InfoTooltipListProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const defaultAriaLabel = ariaLabel || `View all ${title} options with explanations`;
  const iconClassName = iconSize === "sm" ? "h-4 w-4" : "h-5 w-5";

  const content = (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
      {items.map((item, index) => (
        <div key={index} className="space-y-1">
          <h5 className="font-medium text-sm">{item.label}</h5>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {item.description}
          </p>
        </div>
      ))}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center p-0.5 text-muted-foreground hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
          aria-label={defaultAriaLabel}
        >
          <Info className={iconClassName} />
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="h-5 w-5 text-primary" />
                {title}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-2">{content}</div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto p-0.5 text-muted-foreground hover:text-primary hover:bg-transparent"
          aria-label={defaultAriaLabel}
        >
          <Info className={iconClassName} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-4"
        side="right"
        align="start"
        sideOffset={8}
      >
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-medium flex items-center gap-2">
              <Info className="h-4 w-4 text-primary flex-shrink-0" />
              {title}
            </h4>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {content}
        </div>
      </PopoverContent>
    </Popover>
  );
}
